import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import EmptyState from '../../../components/EmptyState';
import StatCard, { StatCardRow } from '../../../components/StatCard';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
} from '../../../components/StandardTable';
import { DownloadIcon, ErrorIcon, ResetIcon, UploadIcon, WarningIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import AdminPageShell from '../../components/AdminPageShell';
import AdminModal from '../../components/AdminModal';
import { formatDate } from '../../lib/format';

type CommitteeRole = 'Chair' | 'Vice-Chair' | 'Member' | 'Ranking Member' | 'Ex Officio';
type FileFormat = 'directory' | 'template';

interface CommitteeMember {
  legislator_id: number;
  seat: string;
  name: string;
  role: CommitteeRole;
}

interface SessionCommittee {
  id: number;
  name: string;
  chamber: string;
  members: CommitteeMember[];
}

interface AssignmentBatch {
  id: string;
  file_name: string | null;
  file_format: FileFormat;
  applied_at: string;
  undone_at: string | null;
  counts: { memberships: number; add: number; remove: number; rerole: number } | null;
  uploaded_by_name: string | null;
}

interface SessionAssignments {
  session_key: string;
  label: string;
  roster_count: number;
  committees: SessionCommittee[];
  batches: AssignmentBatch[];
}

interface PlanMember { legislator_id: number; seat: string | null; name: string | null }

interface PlannedCommittee {
  name: string;
  chamber: string;
  is_new: boolean;
  add: (PlanMember & { role: CommitteeRole })[];
  remove: (PlanMember & { role: CommitteeRole })[];
  rerole: (PlanMember & { from: CommitteeRole; to: CommitteeRole })[];
  unchanged: number;
  members_after: number;
}

interface AssignmentsPlan {
  format: FileFormat;
  hash: string;
  counts: {
    members: number; memberships: number; committees: number; add: number; remove: number; rerole: number;
    new_committees: number; contact_updates: number; errors: number; warnings: number;
  };
  committees: PlannedCommittee[];
  errors: { line: string | null; message: string }[];
  warnings: { line: string | null; message: string }[];
}

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const FORMAT_LABEL: Record<FileFormat, string> = { directory: 'Member directory', template: 'Template' };

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const namesWithRole = (members: CommitteeMember[], role: CommitteeRole) =>
  members.filter((m) => m.role === role).map((m) => `${m.name} (${m.seat})`).join(', ');

const who = (m: PlanMember) => [m.name, m.seat && `(${m.seat})`].filter(Boolean).join(' ');

const Messages: React.FC<{ items: AssignmentsPlan['errors']; error: boolean }> = ({ items, error }) => {
  if (items.length === 0) return null;
  const Icon = error ? ErrorIcon : WarningIcon;
  return (
    <ul className={`space-y-1 rounded-lg border px-3 py-2 text-sm ${error
      ? 'border-error-200 bg-error-50 text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300'
      : 'border-warning-200 bg-warning-50 text-warning-800 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-300'}`}>
      {items.map((item) => (
        <li key={`${item.line}-${item.message}`} className="flex items-start gap-1.5">
          <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{item.line ? `${item.line}: ` : ''}{item.message}</span>
        </li>
      ))}
    </ul>
  );
};

/**
 * Legislature → Sessions → session → Committee assignments
 * (Documentation/ELECTION_OUTCOMES_PRD.md §5.10). Upload the CGA member-directory
 * file or the template; each row matches the session roster by chamber + district.
 * Apply replaces the session's memberships with the file's, and the newest upload
 * can be undone.
 */
const SessionCommitteesPage: React.FC = () => {
  const { sessionKey = '' } = useParams<{ sessionKey: string }>();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [plan, setPlan] = useState<AssignmentsPlan | null>(null);
  const [undoTarget, setUndoTarget] = useState<AssignmentBatch | null>(null);

  const queryKey = ['admin-legislature', 'session-committees', sessionKey] as const;
  const assignments = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await api.get(`/admin/legislature/sessions/${sessionKey}/committees`);
      return response.data.data as SessionAssignments;
    },
    enabled: !!sessionKey,
  });

  const refresh = () => {
    queryClient.invalidateQueries(queryKey);
    // Committee pages and legislator profiles read memberships.
    queryClient.invalidateQueries(['committees']);
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setFile(null);
    setPlan(null);
  };

  const formFor = (extra: Record<string, string> = {}) => {
    const form = new FormData();
    if (file) form.append('file', file);
    Object.entries(extra).forEach(([key, value]) => form.append(key, value));
    return form;
  };

  const templateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.get(`/admin/legislature/sessions/${sessionKey}/committees/template`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `committee-assignments-${sessionKey}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(apiError(error, "Couldn't download the template")),
  });

  const previewMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/admin/legislature/sessions/${sessionKey}/committees/preview`, formFor());
      return response.data.data as AssignmentsPlan;
    },
    onSuccess: setPlan,
    onError: (error) => toast.error(apiError(error, "Couldn't preview the file")),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/admin/legislature/sessions/${sessionKey}/committees/apply`, formFor({ hash: plan?.hash ?? '' }));
      return response.data.data as { counts: AssignmentsPlan['counts'] };
    },
    onSuccess: (data) => {
      toast.success(`Committee assignments replaced: ${data.counts.memberships} membership${data.counts.memberships === 1 ? '' : 's'}`);
      closeUpload();
      refresh();
    },
    onError: (error: any) => {
      toast.error(apiError(error, "Couldn't apply the file"));
      if (error?.response?.status === 409) setPlan(null);
    },
  });

  const undoMutation = useMutation({
    mutationFn: (batchId: string) => api.post(`/admin/legislature/committee-batches/${batchId}/undo`),
    onSuccess: () => {
      toast.success('Upload undone');
      setUndoTarget(null);
      refresh();
    },
    onError: (error) => {
      toast.error(apiError(error, "Couldn't undo the upload"));
      setUndoTarget(null);
    },
  });

  const data = assignments.data;
  const committees = data?.committees ?? [];
  const batches = data?.batches ?? [];
  const newestOpenBatch = batches.find((batch) => !batch.undone_at);
  const memberships = committees.reduce((n, c) => n + c.members.length, 0);
  const changes = plan ? plan.counts.add + plan.counts.remove + plan.counts.rerole + plan.counts.contact_updates : 0;
  const canApply = Boolean(plan) && plan!.counts.errors === 0 && changes > 0;
  const changedCommittees = (plan?.committees ?? []).filter((c) => c.add.length + c.remove.length + c.rerole.length > 0);
  const noRoster = data?.roster_count === 0;

  return (
    <AdminPageShell
      title="Committee Assignments"
      description={data?.label ?? sessionKey}
      backUrl="/admin-next/legislature?tab=sessions"
      actions={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" icon={DownloadIcon} disabled={noRoster}
            loading={templateMutation.isLoading} onClick={() => templateMutation.mutate()}>
            Download Template
          </Button>
          <Button variant="primary" size="sm" icon={UploadIcon} disabled={noRoster} onClick={() => setUploadOpen(true)}>
            Upload Assignments
          </Button>
        </div>
      }
    >
      <div className="card p-5">
        <div className="mb-3 flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Committees this session</h4>
          <HelpTrigger contentKey="committee-assignments-upload" size="sm" ariaLabel="How the committee assignments upload works" />
        </div>

        <StatCardRow className="mb-4">
          <StatCard label="Roster members" value={data?.roster_count ?? 0} loading={assignments.isLoading} />
          <StatCard label="Committees" value={committees.length} loading={assignments.isLoading} />
          <StatCard label="Memberships" value={memberships} loading={assignments.isLoading} />
        </StatCardRow>

        {assignments.isError ? (
          <EmptyState tone="error" size="compact" title="Couldn't load committee assignments" />
        ) : noRoster ? (
          <EmptyState size="compact" title="This session has no roster yet"
            hint="Committee assignments attach to the session's legislators. The roster is generated from officeholder terms when the session's members take office." />
        ) : committees.length === 0 && !assignments.isLoading ? (
          <EmptyState size="compact" title="No committee assignments loaded"
            hint="Upload the General Assembly's member-directory file, or fill in the template, once the chambers announce assignments." />
        ) : (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Committee</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="md">Chamber</StandardTableHeaderCell>
                <StandardTableHeaderCell>Chair</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="lg">Vice-Chair</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Members</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {committees.map((committee) => (
                <StandardTableRow key={committee.id}>
                  <StandardTableCell>{committee.name}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{committee.chamber}</StandardTableCell>
                  <StandardTableCell>{namesWithRole(committee.members, 'Chair') || '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{namesWithRole(committee.members, 'Vice-Chair') || '—'}</StandardTableCell>
                  <StandardTableCell>{committee.members.length}</StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      {batches.length > 0 && (
        <div className="card p-5">
          <h4 className="mb-3 text-sm font-semibold text-text-primary dark:text-text-dark-primary">Uploads</h4>
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Applied</StandardTableHeaderCell>
                <StandardTableHeaderCell>File</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="md">Format</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit" responsiveHide="md">Memberships</StandardTableHeaderCell>
                <StandardTableHeaderCell responsiveHide="md">By</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit"><span className="sr-only">Undo</span></StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {batches.map((batch) => (
                <StandardTableRow key={batch.id}>
                  <StandardTableCell>
                    {formatDate(batch.applied_at)}
                    {batch.undone_at && <span className="ml-2"><Chip label="Undone" tone="neutral" size="sm" /></span>}
                  </StandardTableCell>
                  <StandardTableCell>{batch.file_name ?? '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{FORMAT_LABEL[batch.file_format]}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{batch.counts?.memberships ?? '—'}</StandardTableCell>
                  <StandardTableCell responsiveHide="md">{batch.uploaded_by_name ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    {batch.id === newestOpenBatch?.id && (
                      <Button variant="ghost" size="sm" icon={ResetIcon} onClick={() => setUndoTarget(batch)}>
                        Undo
                      </Button>
                    )}
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        </div>
      )}

      <AdminModal
        isOpen={uploadOpen}
        title="Upload Committee Assignments"
        subtitle={data?.label}
        size="xl"
        onClose={closeUpload}
        footer={<>
          <Button variant="secondary" size="sm" onClick={closeUpload}>Cancel</Button>
          <Button variant="outline" size="sm" disabled={!file} loading={previewMutation.isLoading}
            onClick={() => previewMutation.mutate()}>
            Preview
          </Button>
          <Button variant="primary" size="sm" disabled={!canApply} loading={applyMutation.isLoading}
            onClick={() => applyMutation.mutate()}>
            Replace Assignments
          </Button>
        </>}
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Upload the General Assembly&apos;s <strong>member-directory file</strong> (downloaded from leg.colorado.gov) or
            the <strong>template</strong>. The file <strong>replaces</strong> this session&apos;s assignments: anyone not
            in it ends up on no committees. Nothing changes until you apply.
          </p>
          <div>
            <label htmlFor="committee-file" className={labelClasses}>Assignments file (.csv or .xlsx)</label>
            <input id="committee-file" type="file" accept=".csv,.xlsx" className="input w-full"
              onChange={(event) => { setFile(event.target.files?.[0] ?? null); setPlan(null); }} />
          </div>

          {plan && (
            <>
              <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                Read as: {FORMAT_LABEL[plan.format]} · {plan.counts.members} member{plan.counts.members === 1 ? '' : 's'} ·{' '}
                {plan.counts.memberships} membership{plan.counts.memberships === 1 ? '' : 's'} on {plan.counts.committees} committee{plan.counts.committees === 1 ? '' : 's'}
              </p>
              <StatCardRow minTileWidth="7rem">
                <StatCard label="Added" value={plan.counts.add} variant="success" />
                <StatCard label="Removed" value={plan.counts.remove} variant={plan.counts.remove ? 'warning' : 'default'} />
                <StatCard label="Role changes" value={plan.counts.rerole} variant="info" />
                <StatCard label="New committees" value={plan.counts.new_committees} />
                {plan.format === 'directory' && <StatCard label="Contact updates" value={plan.counts.contact_updates} />}
                <StatCard label="Errors" value={plan.counts.errors} variant={plan.counts.errors ? 'danger' : 'default'} />
              </StatCardRow>

              <Messages items={plan.errors} error />
              <Messages items={plan.warnings} error={false} />

              {changedCommittees.length > 0 ? (
                <StandardTable>
                  <StandardTableHeader>
                    <StandardTableRow>
                      <StandardTableHeaderCell>Committee</StandardTableHeaderCell>
                      <StandardTableHeaderCell>Added</StandardTableHeaderCell>
                      <StandardTableHeaderCell>Removed</StandardTableHeaderCell>
                      <StandardTableHeaderCell responsiveHide="md">Role changes</StandardTableHeaderCell>
                    </StandardTableRow>
                  </StandardTableHeader>
                  <StandardTableBody>
                    {changedCommittees.map((c) => (
                      <StandardTableRow key={`${c.chamber}|${c.name}`}>
                        <StandardTableCell>
                          {c.name}
                          <span className="ml-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">{c.chamber}</span>
                          {c.is_new && <span className="ml-2"><Chip label="New" tone="info" size="sm" /></span>}
                        </StandardTableCell>
                        <StandardTableCell>
                          {c.add.map((m) => `${who(m)}${m.role === 'Member' ? '' : `, ${m.role}`}`).join('; ') || '—'}
                        </StandardTableCell>
                        <StandardTableCell>{c.remove.map(who).join('; ') || '—'}</StandardTableCell>
                        <StandardTableCell responsiveHide="md">
                          {c.rerole.map((m) => `${who(m)}: ${m.from} → ${m.to}`).join('; ') || '—'}
                        </StandardTableCell>
                      </StandardTableRow>
                    ))}
                  </StandardTableBody>
                </StandardTable>
              ) : plan.counts.errors === 0 && (
                <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {changes > 0 ? 'Only contact details change.' : 'The file matches the current assignments; there is nothing to apply.'}
                </p>
              )}
            </>
          )}
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={undoTarget !== null}
        title="Undo this upload?"
        message={`The session's committee assignments, and any contact details it changed, go back to how they were before it. ${undoTarget?.file_name ?? ''}`.trim()}
        confirmText="Undo Upload"
        variant="warning"
        onConfirm={() => undoTarget && undoMutation.mutate(undoTarget.id)}
        onCancel={() => setUndoTarget(null)}
      />
    </AdminPageShell>
  );
};

export default SessionCommitteesPage;
